import React from 'react';
import { Podcast } from '../types/podcast';
import { IconPlay, IconPause, IconTrash } from './Icons';
import './PodcastCard.css';

interface PodcastCardProps {
  podcast: Podcast;
  isPlaying: boolean;
  onPlay: (podcast: Podcast) => void;
  onDelete?: (podcast: Podcast) => void;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  const years = Math.floor(diffDays / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const PodcastCard: React.FC<PodcastCardProps> = ({ podcast, isPlaying, onPlay, onDelete }) => {
  const handleDelete = () => {
    if (!onDelete) return;
    if (window.confirm(`Delete "${podcast.title}"? This will remove the podcast and its audio file permanently.`)) {
      onDelete(podcast);
    }
  };

  return (
    <div className="podcast-card glass">
      <div className="podcast-card-body">
        <h3 className="podcast-card-title">{podcast.title}</h3>
        <p className="podcast-card-summary">{podcast.summary}</p>
        <div className="podcast-card-meta">
          <span className="podcast-card-owner">{podcast.owner}</span>
          <div className="podcast-card-info">
            <span className="duration-badge">{formatDuration(podcast.duration)}</span>
            <span className="podcast-card-date">{formatRelativeDate(podcast.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="podcast-card-footer">
        <button
          className="podcast-card-play"
          onClick={() => onPlay(podcast)}
          aria-label={isPlaying ? `Pause ${podcast.title}` : `Play ${podcast.title}`}
        >
          {isPlaying ? <IconPause /> : <IconPlay />}
        </button>
        <a
          className="podcast-card-download"
          href={podcast.audioUrl}
          download
          aria-label={`Download ${podcast.title}`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>
        {onDelete && (
          <button
            className="podcast-card-delete"
            onClick={handleDelete}
            aria-label={`Delete ${podcast.title}`}
          >
            <IconTrash />
          </button>
        )}
      </div>
    </div>
  );
};

export default PodcastCard;
